import type { JSX } from 'react';
import type { Locale } from '../i18n';
import { widgetText } from './copy';
import { CuentaAtrasDeVuelta } from './CuentaAtrasDeVuelta';
import { MerchantReturnLink, safeMerchantUrl } from './merchantReturn';
import {
  MUTED_TEXT_CLASS,
  PRIMARY_BUTTON_CLASS,
  SCREEN_TITLE_CLASS,
  STACK_CLASS,
  STATUS_AMOUNT_CLASS,
  STATUS_REFERENCE_CLASS,
  SURFACE_CLASS,
  statusBadgeClass,
} from './styles';

export type TerminalStatus = 'success' | 'failed' | 'expired' | 'reversed' | 'blocked';

export interface StatusScreenProps {
  status: TerminalStatus;
  locale: Locale;
  /**
   * Titulo propio, cuando el del estado no dice la verdad. Simetrico con {@link message}, que lleva
   * haciendo lo mismo con el cuerpo desde el principio.
   *
   * <h2>El caso que lo trajo</h2>
   *
   * <p>El comprador que se VA del checkout aterriza en el estado {@code failed} --CANCELED comparte
   * pantalla con FAILED, y eso no cambia: es lo que el resto del widget observa por
   * {@code data-status}--, pero con el cuerpo de la cancelacion. El resultado, visto en QA el
   * 2026-09-23, era una pantalla que se contradecia: de titulo «No pudimos procesar tu pago» y de
   * cuerpo «Cancelaste el pago». Una nos echa la culpa, la otra dice que fue el comprador, y quien
   * lee eso decide si escribe a soporte por un cobro que nadie ha roto.
   *
   * <p>Sin el, manda el del diccionario: esto SUSTITUYE, no decora.
   */
  title?: string | undefined;
  message?: string | undefined;
  /**
   * Codigo estable del catalogo de errores, publicado como `data-code`. En los estados terminales
   * el `GatewayFlow` ya esta desmontado y con el su `ErrorMessage`, asi que esta es la unica
   * superficie donde queda legible el motivo por codigo (y no por el texto, que es traducible).
   */
  code?: string | undefined;
  /**
   * URL de la tienda a la que ofrecer volver. La declara el comercio como `merchant_return_url` al
   * crear el intent; sin ella el comprador se queda en el checkout sin salida natural, que es
   * justo lo que un checkout alojado no debe hacer.
   */
  merchantReturnUrl?: string | undefined;
  /**
   * Segundos tras los que volver SOLO a la tienda. Sin plazo no hay cuenta atras, y no se
   * inventa uno por defecto: quien monta la pantalla es quien sabe si aplica. Empotrado no
   * aplica --la pagina de alrededor ya es del comercio-- y en un rechazo tampoco.
   */
  autoReturnSeconds?: number | undefined;
  /**
   * Que hacer al agotarse la cuenta atras. La navegacion NO la hace esta pantalla: es de
   * presentacion, y ademas "volver" significa cosas distintas segun el modo.
   */
  onAutoReturn?: (() => void) | undefined;
  /**
   * Importe YA FORMATEADO. Se ensena <strong>solo en el pago exitoso</strong>, donde esta pantalla
   * hace de recibo: es el dato que el comprador acaba de mover y el que va a buscar.
   *
   * <p>En un pago FALLIDO se omite a proposito, aunque se pase. Un importe grande junto a «no
   * pudimos procesar tu pago» se lee, en el peor momento posible, como «me cobraron»; y quien mira
   * esa pantalla ya sabe cuanto intentaba pagar.
   */
  amount?: string | undefined;
  /**
   * Identificador publico del cobro (`pi_...`). Es lo unico que el comprador puede citar en un
   * reclamo, y lo que permite al comercio encontrar el cobro sin pedirle mas datos.
   */
  reference?: string | undefined;
}

/**
 * Trazo del icono por estado: check, cruz, reloj, flecha de vuelta y candado.
 *
 * <p>El del exito se dibuja con {@code stroke-dasharray}, asi que su trazo tiene que ser UNA sola
 * linea continua para que la animacion se lea como un gesto y no como varios trozos apareciendo.
 *
 * <p>El de {@code reversed} es una flecha que DESHACE --el arco de vuelta con su punta-- y no una
 * cruz: el pago no fallo, se revirtio.
 */
const ICON_PATH: Record<TerminalStatus, string> = {
  success: 'M20 6 9 17l-5-5',
  failed: 'M18 6 6 18M6 6l12 12',
  expired: 'M12 7v5l3 2M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
  reversed: 'M4 10h10a5 5 0 0 1 0 10h-6M4 10l4-4M4 10l4 4',
  blocked: 'M6 11V8a6 6 0 1 1 12 0v3M5 11h14v10H5V11Z',
};

/**
 * Pantalla terminal del checkout.
 *
 * <h2>Es un recibo, no un cartel</h2>
 *
 * <p>Hasta el 2026-09-09 esta pantalla era un icono de 40 px, un titulo y un boton. Para alguien que
 * acaba de mover dinero eso no cierra nada: no dice cuanto pago ni con que referencia reclamar si
 * manana no le llega el pedido. Ahora el desenlace se compone como un recibo —marca, importe,
 * referencia y salida— con una jerarquia que se lee de un vistazo.
 *
 * <h2>`blocked` no ensena NI UN dato del pago</h2>
 *
 * <p>Y no depende de que quien la monte se acuerde de no pasarlos: los datos se descartan AQUI. Ese
 * estado es el rechazo del frameGuard (plan 7.3.6), o sea una pagina que no deberia estar viendo
 * este checkout; publicar ahi el importe o el identificador seria dar el dato a quien acabamos de
 * decidir que no puede verlo.
 */
export function StatusScreen({
  status,
  locale,
  title,
  message,
  code,
  merchantReturnUrl,
  amount,
  reference,
  autoReturnSeconds,
  onAutoReturn,
}: StatusScreenProps): JSX.Element {
  // La unica puerta: ver el porque en la documentacion de arriba.
  const muestraDatos = status !== 'blocked';
  const importe = muestraDatos && status === 'success' ? amount : undefined;
  const referencia = muestraDatos ? reference : undefined;
  // Los dos textos se resuelven igual --lo propio si lo hay, y si no el del estado-- porque el
  // encabezado y el cuerpo de esta pantalla tienen que poder contarse la misma historia.
  const encabezado = title ?? widgetText(locale, `status.${status}.title`);
  const cuerpo = message ?? widgetText(locale, `status.${status}.body`);

  /*
   * La vuelta automatica es SOLO del desenlace bueno, y las cuatro condiciones importan:
   *
   *   - `success`: en un rechazo o en un expirado el comprador necesita leer el motivo y
   *     decidir si reintenta. Llevarselo de una pantalla de error es hostil, y ademas tira por
   *     tierra el reintento que esa pantalla ofrece.
   *   - una URL http(s) de verdad: sin sitio al que ir no hay nada que contar.
   *   - un plazo explicito: no se inventa uno.
   *   - alguien que sepa navegar: esta pantalla no lo hace.
   */
  const vuelveSolo =
    status === 'success' &&
    autoReturnSeconds !== undefined &&
    onAutoReturn !== undefined &&
    safeMerchantUrl(merchantReturnUrl) !== null;

  return (
    <section
      data-testid="status-screen"
      data-status={status}
      {...(code === undefined || code === '' ? {} : { 'data-code': code })}
      role="status"
      aria-live="polite"
      className={`${SURFACE_CLASS} ${STACK_CLASS} items-center text-center`}
    >
      <span className={statusBadgeClass(status)}>
        <svg
          viewBox="0 0 24 24"
          width="28"
          height="28"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {/*
           * El trazo del exito se DIBUJA. Es la unica animacion de contenido del widget y dura
           * menos de medio segundo: marca el instante en que el pago se cierra, que es lo que el
           * comprador esta esperando. `prefers-reduced-motion` la neutraliza en styles.css, y el
           * check queda dibujado igual porque la animacion termina en su estado final.
           */}
          <path d={ICON_PATH[status]} className={status === 'success' ? 'apipay-draw' : undefined} />
        </svg>
      </span>

      <h2 className={SCREEN_TITLE_CLASS}>{encabezado}</h2>

      {importe === undefined || importe === '' ? null : (
        <p data-testid="status-amount" className={STATUS_AMOUNT_CLASS}>
          {importe}
        </p>
      )}

      {cuerpo === '' ? null : <p className={MUTED_TEXT_CLASS}>{cuerpo}</p>}

      {referencia === undefined || referencia === '' ? null : (
        <p data-testid="status-reference" className={STATUS_REFERENCE_CLASS}>
          {/*
           * `select-all` para que un toque seleccione la referencia entera: se cita por telefono o
           * se pega en un correo, y en un movil seleccionar una cadena larga a mano es un suplicio.
           */}
          <span className="select-all">{referencia}</span>
        </p>
      )}

      <MerchantReturnLink
        merchantReturnUrl={merchantReturnUrl}
        locale={locale}
        className={PRIMARY_BUTTON_CLASS}
      />

      {vuelveSolo ? (
        <CuentaAtrasDeVuelta
          segundos={autoReturnSeconds}
          locale={locale}
          onFinalizar={onAutoReturn}
        />
      ) : null}
    </section>
  );
}
